import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/apiClient";
import { RefreshCw, Plus, Pencil, Trash2, Plug, CheckCircle2, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const PARITY_OPTIONS = [
  { value: "parity", label: "Rate parity (push BAR rate)" },
  { value: "allow_lower", label: "Allow lower rates" },
  { value: "allow_higher", label: "Allow higher rates" },
];

const ROOM_TYPES = ["Standard", "Deluxe Double", "Superior King", "Studio Suite", "Presidential Suite"];

export default function Channels() {
  const [data, setData] = useState(null);
  const [inv, setInv] = useState(null);
  const [logs, setLogs] = useState([]);
  const [otaTypes, setOtaTypes] = useState([]);
  const [selectedRT, setSelectedRT] = useState("Deluxe Double");
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [perSyncing, setPerSyncing] = useState({});
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id,...}=edit
  const [deleting, setDeleting] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [{ data: chs }, { data: ivd }, { data: lg }] = await Promise.all([
        api.get("/channels"),
        api.get("/channels/inventory", { params: { room_type: selectedRT } }),
        api.get("/channels/sync-logs", { params: { limit: 10 } }),
      ]);
      setData(chs); setInv(ivd); setLogs(lg.logs || []);
    } catch (e) { toast.error("Failed to load channels"); }
    finally { setLoading(false); }
  }, [selectedRT]);

  useEffect(() => { loadAll(); }, [loadAll]);
  useEffect(() => {
    api.get("/channels/ota-types").then(({ data }) => setOtaTypes(data.ota_types || [])).catch(() => {});
  }, []);

  async function toggleStatus(ch) {
    const next = ch.status === "live" ? "paused" : "live";
    try {
      await api.patch(`/channels/${ch.id}`, { status: next });
      toast.success(`${ch.name} → ${next}`); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  async function syncAll() {
    setSyncing(true);
    try {
      const { data: r } = await api.post("/channels/sync");
      toast.success(`Synced ${r.channels} channels · ${r.items_pushed} items pushed`);
      loadAll();
    } catch (e) { toast.error("Sync failed"); }
    finally { setSyncing(false); }
  }

  async function syncOne(ch) {
    setPerSyncing((s) => ({ ...s, [ch.id]: true }));
    try {
      const { data: r } = await api.post(`/channels/${ch.id}/sync`);
      toast.success(`${ch.name} synced · ${r.items_pushed} items`); loadAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Sync failed"); }
    finally { setPerSyncing((s) => ({ ...s, [ch.id]: false })); }
  }

  async function handleDelete() {
    if (!deleting) return;
    try {
      await api.delete(`/channels/${deleting.id}`);
      toast.success(`${deleting.name} removed`); setDeleting(null); loadAll();
    } catch (e) { toast.error("Delete failed"); }
  }

  if (loading || !data) return <div className="text-white/50">Loading channels…</div>;

  return (
    <div className="space-y-8" data-testid="channels-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-2">Channel Manager</div>
          <h1 className="font-display text-3xl md:text-4xl">OTA Connections</h1>
          <p className="text-white/50 text-sm mt-2">Configure OTA channels, push inventory & rates, sync with the booking engine.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setEditing({})} data-testid="add-channel-btn"
            className="rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10">
            <Plus className="w-3.5 h-3.5" /> Add channel
          </button>
          <button onClick={syncAll} disabled={syncing} data-testid="sync-all-btn"
            className="btn-gold rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-60">
            <RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> {syncing ? "Syncing…" : "Sync all"}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-5">
        <Stat label="Connected channels" value={`${data.summary.connected}/${data.summary.total}`} />
        <Stat label="Bookings today" value={data.summary.bookings_today} />
        <Stat label="Avg rate pushed" value={`₹${data.summary.avg_rate_pushed.toLocaleString("en-IN")}`} />
        <Stat label="Last sync" value={data.summary.last_sync ? new Date(data.summary.last_sync).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "—"} />
      </div>

      <div>
        <h2 className="font-display text-xl mb-4">Channels</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {data.items.map((ch) => (
            <div key={ch.id} data-testid={`channel-card-${ch.name.replace(/[^a-zA-Z0-9]/g, "-")}`}
              className="bg-[#12182B] border border-white/5 rounded-xl p-5 flex flex-col">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="font-display text-lg">{ch.name}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 mt-1">{ch.ota_type}</div>
                </div>
                <span className={`text-[10px] tracking-widest uppercase px-2 py-1 rounded ${ch.status === "live" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20" : "bg-white/5 text-white/50 border border-white/10"}`}>{ch.status}</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                <Info label="Bookings/30d" value={ch.bookings_month || 0} />
                <Info label="Commission" value={`${ch.commission_pct ?? 0}%`} />
                <Info label="Markup" value={`${ch.markup_pct ?? 0}%`} />
                <Info label="Parity" value={(ch.parity_mode || "parity").replace("_", " ")} />
              </div>
              <div className="text-[11px] text-white/40 mb-3">
                {ch.property_id ? `PID: ${ch.property_id}` : <span className="text-amber-300/70">Not configured</span>}
                {ch.last_sync_at && <span className="ml-2">· {new Date(ch.last_sync_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}</span>}
              </div>
              <div className="mt-auto flex items-center gap-2 pt-3 border-t border-white/5">
                <button onClick={() => toggleStatus(ch)} data-testid={`toggle-${ch.id}`}
                  className="text-xs text-[#D4AF37] hover:text-[#F2D06B]">
                  {ch.status === "live" ? "Pause" : "Activate"}
                </button>
                <button onClick={() => syncOne(ch)} disabled={ch.status !== "live" || perSyncing[ch.id]} data-testid={`sync-${ch.id}`}
                  className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 disabled:opacity-30 disabled:cursor-not-allowed">
                  <RefreshCw className={`w-3 h-3 ${perSyncing[ch.id] ? "animate-spin" : ""}`} /> Sync
                </button>
                <div className="flex-1" />
                <button onClick={() => setEditing(ch)} title="Edit" data-testid={`edit-${ch.id}`}
                  className="text-white/40 hover:text-white"><Pencil className="w-3.5 h-3.5" /></button>
                <button onClick={() => setDeleting(ch)} title="Delete" data-testid={`delete-${ch.id}`}
                  className="text-white/40 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="inventory-push">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
          <h2 className="font-display text-xl">Inventory & Rate Push · Next 7 days</h2>
          <select value={selectedRT} onChange={(e) => setSelectedRT(e.target.value)} data-testid="room-type-select"
            className="bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2 text-sm">
            {ROOM_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        {inv && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-white/50 text-xs uppercase tracking-widest border-b border-white/5">
                  <th className="px-3 py-3">Date</th>
                  <th className="px-3 py-3">Avail</th>
                  <th className="px-3 py-3">BAR Rate</th>
                  {(inv.channels || []).map((c) => (
                    <th key={c.id} className="px-3 py-3">{c.name}<span className="text-white/30 ml-1 normal-case">({c.markup_pct >= 0 ? "+" : ""}{c.markup_pct}%)</span></th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {inv.rows.map((r) => (
                  <tr key={r.date} className="border-b border-white/5 last:border-none hover:bg-[#1A2235]">
                    <td className="px-3 py-3">{r.date}</td>
                    <td className="px-3 py-3 font-medium">{r.available}</td>
                    <td className="px-3 py-3">₹{r.bar_rate.toLocaleString("en-IN")}</td>
                    {(inv.channels || []).map((c) => (
                      <td key={c.id} className="px-3 py-3 text-white/70">₹{(r.rates?.[c.name] ?? r.bar_rate).toLocaleString("en-IN")}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {(inv.channels || []).length === 0 && (
              <div className="text-white/40 text-sm text-center py-6">No live channels. Activate at least one to push rates.</div>
            )}
          </div>
        )}
      </div>

      <div className="bg-[#12182B] border border-white/5 rounded-xl p-6" data-testid="sync-history">
        <div className="flex items-center gap-2 mb-4">
          <History className="w-4 h-4 text-[#D4AF37]" />
          <h2 className="font-display text-xl">Recent sync activity</h2>
        </div>
        {logs.length === 0 ? (
          <div className="text-white/40 text-sm">No sync events yet. Hit "Sync all" to push your first batch.</div>
        ) : (
          <ul className="divide-y divide-white/5">
            {logs.map((l) => (
              <li key={l.id} className="py-3 flex items-start gap-3 text-sm">
                {l.status === "success"
                  ? <CheckCircle2 className="w-4 h-4 text-emerald-300 mt-0.5 shrink-0" />
                  : <AlertTriangle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />}
                <div className="flex-1">
                  <div className="text-white/90">{l.channel_name} <span className="text-white/40">· {l.message}</span></div>
                  <div className="text-xs text-white/40 mt-0.5">{new Date(l.created_at).toLocaleString("en-IN")} · {l.items_pushed} items</div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ChannelDialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}
        initial={editing} otaTypes={otaTypes} onSaved={loadAll} />

      <AlertDialog open={!!deleting} onOpenChange={(o) => { if (!o) setDeleting(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              This deletes the OTA configuration. Reservations already received from this channel are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} data-testid="delete-confirm-btn"
              className="bg-red-500/80 hover:bg-red-500">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-[#12182B] border border-white/5 rounded-xl p-5">
      <div className="text-xs uppercase tracking-widest text-white/50">{label}</div>
      <div className="font-display text-3xl mt-3">{value}</div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-[#0a0f1c]/60 rounded-md px-2.5 py-1.5">
      <div className="text-[10px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="text-white/90">{value}</div>
    </div>
  );
}

function ChannelDialog({ open, onOpenChange, initial, otaTypes, onSaved }) {
  const isEdit = !!initial?.id;
  const [form, setForm] = useState(emptyForm());
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);

  function emptyForm() {
    return {
      name: "", ota_type: "Booking.com", property_id: "",
      api_username: "", api_password: "",
      commission_pct: 15, markup_pct: 0, parity_mode: "parity",
      room_mappings: ROOM_TYPES.map((t) => ({ room_type: t, ota_room_code: "" })),
      notes: "", status: "paused",
    };
  }

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      const existing = initial.room_mappings || [];
      const merged = ROOM_TYPES.map((t) => existing.find((m) => m.room_type === t) || { room_type: t, ota_room_code: "" });
      setForm({
        name: initial.name || "",
        ota_type: initial.ota_type || "Other",
        property_id: initial.property_id || "",
        api_username: initial.api_username || "",
        api_password: "",
        commission_pct: initial.commission_pct ?? 15,
        markup_pct: initial.markup_pct ?? 0,
        parity_mode: initial.parity_mode || "parity",
        room_mappings: merged,
        notes: initial.notes || "",
        status: initial.status || "paused",
      });
    } else {
      setForm(emptyForm());
    }
    setTestResult(null);
  }, [open, isEdit, initial]);

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }
  function updateMapping(idx, code) {
    setForm((f) => ({ ...f, room_mappings: f.room_mappings.map((m, i) => i === idx ? { ...m, ota_room_code: code } : m) }));
  }

  async function save() {
    if (!form.name.trim()) { toast.error("Channel name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        commission_pct: Number(form.commission_pct),
        markup_pct: Number(form.markup_pct),
        room_mappings: form.room_mappings.filter((m) => m.ota_room_code.trim()),
      };
      if (isEdit) {
        await api.put(`/channels/${initial.id}`, payload);
        toast.success(`${form.name} updated`);
      } else {
        await api.post("/channels", payload);
        toast.success(`${form.name} added`);
      }
      onSaved(); onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  }

  async function testConnection() {
    if (!isEdit) { toast.info("Save the channel first, then test."); return; }
    setTesting(true); setTestResult(null);
    try {
      const { data } = await api.post(`/channels/${initial.id}/test-connection`);
      setTestResult(data);
    } catch (e) {
      setTestResult({ ok: false, message: e.response?.data?.detail || "Test failed" });
    } finally { setTesting(false); }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#0F1424] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="channel-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{isEdit ? `Edit ${initial.name}` : "Add OTA channel"}</DialogTitle>
          <DialogDescription className="text-white/50">
            Connect an OTA manually. Inventory & rates push on every sync; bookings flow back into the engine.
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Channel name *">
            <Input value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="e.g. Booking.com — Main" data-testid="field-name"
              className="bg-[#0a0f1c] border-white/10" />
          </Field>
          <Field label="OTA platform">
            <select value={form.ota_type} onChange={(e) => update("ota_type", e.target.value)} data-testid="field-ota-type"
              className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2 text-sm">
              {(otaTypes.length ? otaTypes : ["Booking.com", "Other"]).map((t) => <option key={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Property / Hotel ID">
            <Input value={form.property_id} onChange={(e) => update("property_id", e.target.value)} placeholder="e.g. 1234567 or B-XXXX" data-testid="field-property-id"
              className="bg-[#0a0f1c] border-white/10" />
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={(e) => update("status", e.target.value)} data-testid="field-status"
              className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2 text-sm">
              <option value="paused">Paused</option>
              <option value="live">Live</option>
            </select>
          </Field>
          <Field label="API username">
            <Input value={form.api_username} onChange={(e) => update("api_username", e.target.value)} placeholder="Partner credential" data-testid="field-api-user"
              className="bg-[#0a0f1c] border-white/10" />
          </Field>
          <Field label={isEdit ? "API password (leave blank to keep)" : "API password / key"}>
            <Input type="password" value={form.api_password} onChange={(e) => update("api_password", e.target.value)} placeholder="••••••••" data-testid="field-api-pass"
              className="bg-[#0a0f1c] border-white/10" />
          </Field>
          <Field label="Commission %">
            <Input type="number" min="0" max="100" step="0.5" value={form.commission_pct} onChange={(e) => update("commission_pct", e.target.value)} data-testid="field-commission"
              className="bg-[#0a0f1c] border-white/10" />
          </Field>
          <Field label="Rate markup % (over BAR)">
            <Input type="number" min="-50" max="200" step="0.5" value={form.markup_pct} onChange={(e) => update("markup_pct", e.target.value)} data-testid="field-markup"
              className="bg-[#0a0f1c] border-white/10" />
          </Field>
          <Field label="Rate parity mode" className="md:col-span-2">
            <select value={form.parity_mode} onChange={(e) => update("parity_mode", e.target.value)} data-testid="field-parity"
              className="w-full bg-[#0a0f1c] border border-white/10 rounded-md px-3 py-2 text-sm">
              {PARITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </Field>
        </div>

        <div className="mt-2">
          <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Room type mapping</div>
          <div className="bg-[#0a0f1c]/60 rounded-md border border-white/5 divide-y divide-white/5">
            {form.room_mappings.map((m, idx) => (
              <div key={m.room_type} className="flex items-center gap-3 px-3 py-2">
                <div className="text-sm w-1/2 text-white/80">{m.room_type}</div>
                <Input value={m.ota_room_code} onChange={(e) => updateMapping(idx, e.target.value)}
                  placeholder="OTA room code" data-testid={`field-map-${m.room_type.replace(/ /g, "-")}`}
                  className="bg-[#0a0f1c] border-white/10 flex-1" />
              </div>
            ))}
          </div>
        </div>

        <Field label="Notes">
          <Textarea value={form.notes} onChange={(e) => update("notes", e.target.value)} rows={2} data-testid="field-notes"
            className="bg-[#0a0f1c] border-white/10" placeholder="Internal notes about this connection" />
        </Field>

        {testResult && (
          <div className={`text-sm rounded-md px-3 py-2 border ${testResult.ok ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-200" : "bg-amber-500/10 border-amber-500/30 text-amber-200"}`} data-testid="test-result">
            <div className="flex items-center gap-2">{testResult.ok ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}<span>{testResult.message}</span></div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <button onClick={testConnection} disabled={testing || !isEdit} data-testid="test-connection-btn"
            className="rounded-md px-4 py-2 text-sm inline-flex items-center gap-2 border border-white/10 text-white/80 hover:bg-white/5 disabled:opacity-40">
            <Plug className={`w-3.5 h-3.5 ${testing ? "animate-pulse" : ""}`} /> {testing ? "Testing…" : "Test connection"}
          </button>
          <button onClick={() => onOpenChange(false)} data-testid="cancel-btn"
            className="rounded-md px-4 py-2 text-sm border border-white/10 text-white/70 hover:bg-white/5">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="save-channel-btn"
            className="btn-gold rounded-md px-4 py-2 text-sm disabled:opacity-60">
            {saving ? "Saving…" : isEdit ? "Save changes" : "Add channel"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs uppercase tracking-widest text-white/50 mb-1.5 block">{label}</Label>
      {children}
    </div>
  );
}
